import { Course } from '@components/courses/course-card'
import { getCourseNameFromAPI } from '@lib/constants/booking'
import { SUBJECT_LABEL_OVERRIDES } from './course-filters'

const CAMP_TAG_SLUG = 'camp-course'
const CAMP_URL_SEGMENT = '/kids-teens-coding-camps/'

export const isCampCourse = (course: Course): boolean => {
  const hasCampTag =
    course.tags?.some((tag) => tag.slug === CAMP_TAG_SLUG) ?? false
  const hasCampUrl = (course.landingPageUrl ?? '').includes(CAMP_URL_SEGMENT)

  return hasCampTag || hasCampUrl
}

export type CourseGroup = {
  key: string
  title: string
  titleId?: string
  link?: string
  courses: Course[]
}

// Order in which subject categories should appear on the courses page.
export const SUBJECT_ORDER = ['Coding', 'Math', 'ELA']

// Landing pages each group's heading links to (and that the SEO Collection
// exposes as its url). ELA doesn't have a dedicated page yet - the closest
// existing page is branded specifically for public speaking, not ELA as a
// whole, so that group stays unlinked rather than pointing there. Every
// camp group links to the camps hub instead of these.
const SUBJECT_COURSE_LINKS: Partial<Record<string, string>> = {
  Coding: '/coding-for-kids/',
  Math: '/courses/math/',
}

const buildGroupsBySubject = (
  courses: Course[],
  suffix: string
): CourseGroup[] => {
  const link = suffix === 'Camps' ? CAMP_URL_SEGMENT : undefined

  // key includes the suffix so a subject's course group and camp group
  // (e.g. "Coding Courses" and "Coding Camps") never collide as React keys.
  const groups: (CourseGroup & { subject: string })[] = SUBJECT_ORDER.map(
    (subject) => ({
      key: `${subject}-${suffix}`,
      subject,
      title: `${
        SUBJECT_LABEL_OVERRIDES[subject] ?? getCourseNameFromAPI(subject)
      } ${suffix}`,
      link: link ?? SUBJECT_COURSE_LINKS[subject],
      courses: [],
    })
  )

  courses.forEach((course) => {
    groups
      .find((group) => group.subject === course.subject)
      ?.courses.push(course)
  })

  return groups.filter((group) => group.courses.length > 0)
}

export const groupCoursesBySubject = (courses: Course[]): CourseGroup[] =>
  buildGroupsBySubject(courses, 'Courses')

export const groupCampCoursesBySubject = (courses: Course[]): CourseGroup[] =>
  buildGroupsBySubject(courses, 'Camps')
